import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({ theme: "light", cycleTheme: () => {}, toggleTheme: () => {} });
const STORAGE_KEY = "dhp-theme";
export const THEMES = ["light", "dark", "premium"];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    const stored = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : "light";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Listen for cross-component theme signals (e.g. after login → default to premium)
  useEffect(() => {
    const onThemeSignal = (e) => {
      const next = e?.detail;
      if (THEMES.includes(next)) setTheme(next);
    };
    window.addEventListener("dhp:theme", onThemeSignal);
    return () => window.removeEventListener("dhp:theme", onThemeSignal);
  }, []);

  // Cycle: light → dark → premium → light
  const cycleTheme = useCallback(() => {
    setTheme((t) => {
      const i = THEMES.indexOf(t);
      return THEMES[(i + 1) % THEMES.length];
    });
  }, []);

  // Backward-compat toggle (light ↔ dark) kept for internal shortcut users.
  const toggleTheme = cycleTheme;

  return (
    <ThemeContext.Provider value={{ theme, cycleTheme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
